import React, { useEffect, useState } from "react";
import Pagination from "../../components/Pagination";
import { API_BASE } from "../../utils/api";
import api from "../../utils/api";
import "react-toastify/dist/ReactToastify.css";
import { toast, ToastContainer } from "react-toastify";
import { getAppConfig } from "../../utils/appConfig";

function TableSub({ searchTerm }) {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [currentSubCategory, setCurrentSubCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;

  // Touch handling for mobile
  const [touchStartY, setTouchStartY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [editOtherLang, setEditOtherLang] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("en");

  // holds all language values
  const [subCatTranslations, setSubCatTranslations] = useState({});
  const { defaultImage } = getAppConfig();
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/languages/availableLanguages`);
        const data = await res.json();
        setLanguages(data);
      } catch (err) {
        console.error("Failed to load languages", err);
      }
    };
    fetchLanguages();
  }, []);

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setTouchStartY(e.touches[0].clientY);
      setIsScrolling(false);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
      if (deltaY > 10) {
        setIsScrolling(true);
      }
    }
  };

  const handleTouchEnd = (e, subCat) => {
    if (!isScrolling) {
      handleViewClick(subCat, e);
    }
  };

  // Fetch subcategories and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const subCatResponse = await api.get("/subcategory/display");
        const normalizedSubCats = subCatResponse.data.map((sc) => ({
          ...sc,

          // normalize subCat for UI
          _i18n: sc.subCat || {},
          subCat:
            typeof sc.subCat === "string" ? sc.subCat : sc.subCat?.en || "",

          category: sc.category
            ? {
                ...sc.category,
                _i18n: sc.category.name || {},
                name:
                  typeof sc.category.name === "string"
                    ? sc.category.name
                    : sc.category.name?.en || "",
              }
            : null,
        }));

        setSubCategories(normalizedSubCats);

        const catResponse = await api.get("/categories/all");

        const normalizedCategories = catResponse.data.map((cat) => ({
          ...cat,
          _i18n: cat.name || {},
          name: typeof cat.name === "string" ? cat.name : cat.name?.en || "",
        }));

        setCategories(normalizedCategories);
      } catch (err) {
        setError("Failed to load categories or subcategories");
        console.error(err);
      }
    };

    fetchData();
  }, []);

  const filteredSubCategories = subCategories.filter((subCat) => {
    const categoryName = subCat.category?.name?.toLowerCase() || "";
    const subCatName = subCat.subCat?.toLowerCase() || "";
    const lowerSearchTerm = searchTerm.toLowerCase();
    return (
      categoryName.includes(lowerSearchTerm) ||
      subCatName.includes(lowerSearchTerm)
    );
  });

  // Recalculate total pages based on filtered results
  const filteredTotalPages = Math.ceil(
    filteredSubCategories.length / itemsPerPage,
  );

  const paginatedSubCategories = filteredSubCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const capitalize = (value) => {
    if (typeof value !== "string") return "";
    if (!value.length) return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  // Open modal safely
  const handleViewClick = (subCat, e) => {
    if (e) e.stopPropagation();

    // build translations map from DB
    const initialTranslations = {};
    languages.forEach((lang) => {
      initialTranslations[lang.code] = subCat._i18n?.[lang.code] || "";
    });

    // ensure EN exists
    initialTranslations.en = subCat.subCat || "";

    setSubCatTranslations(initialTranslations);
    setSelectedLang("en");
    setEditOtherLang(false);

    setCurrentSubCategory({
      ...subCat,
      subCat: initialTranslations.en,
    });

    setTimeout(() => setOpenModal(true), 100);
  };

  const handleFieldChange = (field, value) => {
    setCurrentSubCategory((prev) => {
      if (field === "category") {
        // value is already the selected category object
        return { ...prev, category: value };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setCurrentSubCategory((prev) => ({
        ...prev,
        newImage: e.target.files[0],
        newImagePreview: URL.createObjectURL(e.target.files[0]),
      }));
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("subCat", JSON.stringify(subCatTranslations));

    formData.append("category", currentSubCategory.category._id);
    formData.append("isVisible", currentSubCategory.isVisible);

    if (currentSubCategory.newImage) {
      formData.append("image", currentSubCategory.newImage);
    } else if (currentSubCategory.image) {
      formData.append("image", currentSubCategory.image);
    }

    try {
      const response = await api.put(
        `/subcategory/${currentSubCategory._id}`,
        formData,
      );

      const updated = response.data;

      const normalizedUpdated = {
        ...updated,
        _i18n: updated.subCat || {},
        subCat:
          typeof updated.subCat === "string"
            ? updated.subCat
            : updated.subCat?.en || "",
        category: updated.category
          ? {
              ...updated.category,
              _i18n: updated.category.name || {},
              name:
                typeof updated.category.name === "string"
                  ? updated.category.name
                  : updated.category.name?.en || "",
            }
          : null,
      };

      setSubCategories((prev) =>
        prev.map((subCat) =>
          subCat._id === normalizedUpdated._id ? normalizedUpdated : subCat,
        ),
      );

      toast.success("Subcategory updated successfully!");
      setTimeout(() => {
        window.location.reload();
      }, 500);
      setOpenModal(false);
    } catch (err) {
      console.error("Error updating subcategory:", err);
      toast.error("Failed to update subcategory.");
    }
  };

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
          {paginatedSubCategories.length > 0 ? (
            paginatedSubCategories.map((subCat) => (
              <div
                key={subCat._id}
                onClick={(e) => handleViewClick(subCat, e)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={(e) => handleTouchEnd(e, subCat)}
                className={`relative h-72 cursor-pointer flex flex-col rounded-lg shadow-md transition-transform hover:scale-105 ${
                  subCat.isVisible
                    ? "bg-white border border-gray-200"
                    : "bg-red-50 border-4 border-red-500"
                }`}
              >
                {!subCat.isVisible && (
                  <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center text-white font-bold text-xl uppercase z-10 rounded-lg">
                    Not Available
                  </div>
                )}
                <img
                  src={
                    subCat.image ? `${API_BASE}${subCat.image}` : defaultImage
                  }
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultImage;
                  }}
                  alt={subCat.subCat || "Subcategory Image"}
                  className="w-full h-40 object-cover rounded-t-lg"
                />
                <div className="flex-1 p-3 flex flex-col justify-start bg-gradient-to-br from-gray-50 to-white rounded-b-lg">
                  <h3
                    className="font-bold text-sm line-clamp-2 h-10 overflow-hidden text-gray-800"
                    title={subCat.subCat}
                  >
                    {capitalize(subCat.subCat)}
                  </h3>
                  <p
                    className="text-gray-500 text-xs mt-1 line-clamp-1 overflow-hidden"
                    title={subCat.category?.name}
                  >
                    {capitalize(subCat.category?.name)}
                  </p>
                </div>
              </div>
            ))
          ) : searchTerm.trim() !== "" ? (
            <div className="col-span-full text-center py-8">
              <h6 className="text-xl text-gray-700">No results found</h6>
            </div>
          ) : null}
        </div>

        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalPages={filteredTotalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setOpenModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {currentSubCategory && (
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-4">
                  Edit Subcategory
                </h2>

                <div className="mb-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editOtherLang}
                      onChange={(e) => setEditOtherLang(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Edit another language
                    </span>
                  </label>
                </div>

                {editOtherLang && (
                  <div className="flex gap-2 mb-4 flex-wrap">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setSelectedLang(lang.code);
                          setCurrentSubCategory((prev) => ({
                            ...prev,
                            subCat: subCatTranslations[lang.code] || "",
                          }));
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                          selectedLang === lang.code
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategory Name ({selectedLang.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    value={currentSubCategory.subCat || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCurrentSubCategory((prev) => ({
                        ...prev,
                        subCat: value,
                      }));
                      setSubCatTranslations((prev) => ({
                        ...prev,
                        [selectedLang]: value,
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={currentSubCategory.category?._id || ""}
                    onChange={(e) => {
                      const selected = categories.find(
                        (cat) => cat._id === e.target.value,
                      );
                      handleFieldChange("category", selected);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {capitalize(category.name)}
                      </option>
                    ))}
                  </select>
                </div>

                {currentSubCategory.image && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Current Image:
                    </p>
                    <img
                      src={`${API_BASE}${currentSubCategory.image}`}
                      alt="Subcategory"
                      className="w-40 h-40 object-cover rounded-lg border border-gray-200"
                    />
                  </div>
                )}

                {currentSubCategory.newImagePreview && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      New Image:
                    </p>
                    <img
                      src={currentSubCategory.newImagePreview}
                      alt="Preview"
                      className="w-40 h-40 object-cover rounded-lg border border-gray-200"
                    />
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Image
                  </label>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>

                <div className="mb-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentSubCategory.isVisible || false}
                      onChange={(e) =>
                        handleFieldChange("isVisible", e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Available
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleSubmit}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                >
                  Submit
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={500} />
    </div>
  );
}

export default TableSub;
